import { useMemo, useEffect, useRef } from "react";
import Link from "next/link";
import capitalize from "lodash/capitalize";
import { useSelector } from "react-redux";
import isEmpty from "lodash/isEmpty";
import classnames from "classnames";

import useRouter from "@stadium/hooks/lib/use-custom-route";
import { useCreateSwagContext } from "@contexts/create-swag-context";
import { useStoreContext } from "@components/store/provider";
import { useMenuAnalytics } from "@hooks/analytics/use-menu-analytics";

import { GetScreenType } from "@stadium/helpers/device";
import { tabletScreenType, breakpoints } from "@stadium/helpers/style";
import { checkIfMenuExists, getParentCategoryFromCategory } from "@helpers/menu";

import DropdownComponent, {
  DropdownItem,
  DropdownToggle,
} from "@stadium/ui/src/components/bootstrap-dropdown";
import HeaderContent from "@stadium/ui/src/components/HeaderContent";
import EditCategories from "@components/store/multi-level-menu/edit-categories";
import Multimedia from "@sharedComponents/multimedia";
import PaginationSlider from "@stadium/ui/src/components/pagination-slider";

import { getModifiedMenuList } from "@components/store/multi-level-menu/serializer";

import { getMenu, getMenuCodeMapping } from "@selectors/menu";
import { getStoreShowCategoryImages } from "@selectors/store";

import { ALL_SUBCATEGORY_CODE, getMenuItemRoute } from "@constants/routes";
import { DEFAULT_MENU_ITEM_CODE } from "@constants/menu";
import { OTHERS_CATEGORY_CODE } from "@components/store/multi-level-menu/constants";

import sampleImage from "@images/no-image.jpg";

import styles from "./main.module.scss";

/* Menu Convention 

For Multi-tier Menu (3-level menu)
URL -> /food-and-beverages--personal-box/curated-medley
parentCategoryCode -> food-and-beverages
activeCategoryCode -> personal-box
subCategory -> curated-medley

For Normal Menu (2-level menu)
URL -> /food-and-beverages/personal-box
parentCategoryCode -> default
activeCategoryCode -> food-and-beverages
subCategory -> personal-box
*/

const MultiLevelMenu = ({ scrollable = false }) => {
  const { trackMainCategory, trackSubcategory } = useMenuAnalytics();

  const router = useRouter();
  const paginatedSliderRef = useRef(null);

  const {
    storeBasePath,
    persistedFiltersQuery,
    isPreviewCatalogMode,
    category,
    subCategory,
    countryCode,
    isNewSwagmagicHost,
    themeCollectionItems,
  } = useStoreContext();

  const { isSwagCreationShopFlow } = useCreateSwagContext();
  const themedCollections = useMemo(
    () => themeCollectionItems?.map((item) => item.code) || [],
    [themeCollectionItems],
  );

  const orderedMenuItem = useSelector(getMenu);

  const storeShowCategoryImages = useSelector(getStoreShowCategoryImages);
  const showCategoryImages = storeShowCategoryImages;

  const menuCodeMapping = useSelector(getMenuCodeMapping);

  const totalLevel = orderedMenuItem?.totalLevel;

  const shouldShowMenuItemImage = (currentLevel) => {
    if (!showCategoryImages || currentLevel === 1) return false;
    if (currentLevel === 2 && visibleDepth === 3) {
      return false;
    }
    return true;
  };

  const getMenuItemImageClass = (currentLevel, parentCode = "") => {
    if (!shouldShowMenuItemImage(currentLevel)) return "";
    return themedCollections.includes(parentCode)
      ? styles.themedCollectionActive
      : styles.nonThemedCollectionActive;
  };

  const isMultiLevelMenu = orderedMenuItem?.totalLevel > 2;
  const skipTitleHumanization = orderedMenuItem?.skipTitleHumanization;

  const { parentCategoryCode, activeCategoryCode } = getParentCategoryFromCategory(category);

  const activeLevelCategory = useMemo(
    () => ({
      1: parentCategoryCode === DEFAULT_MENU_ITEM_CODE ? activeCategoryCode : parentCategoryCode,
      2: parentCategoryCode === DEFAULT_MENU_ITEM_CODE ? subCategory : activeCategoryCode,
      3: subCategory,
    }),
    [parentCategoryCode, activeCategoryCode, subCategory],
  );

  const levelClassMapping = {
    1: styles.levelOne,
    2: styles.levelTwo,
    3: styles.levelThree,
  };

  const menuList = orderedMenuItem?.children || [];

  const { lg, xl } = breakpoints;

  // Considering a range of 992px to 1200px as the tablet view dimension, we can reduce the number of menu items in the row. Otherwise, these dimensions will be considered as desktop.
  const deviceType = GetScreenType({
    breakpoints: {
      md: lg,
      lg: xl,
    },
  });

  const finalList = useMemo(
    () =>
      getModifiedMenuList({
        list: orderedMenuItem?.children,
        parentItemCode: "",
        level: 1,
        isPreviewCatalogMode,
        isMultiLevelMenu,
        totalLevel,
        isTabletDevice: deviceType === tabletScreenType,
        forDynamicSlices: scrollable,
      }),
    [orderedMenuItem, isPreviewCatalogMode, deviceType, scrollable],
  );

  useEffect(() => {
    if (
      menuList.length > 0 &&
      !isEmpty(menuCodeMapping) &&
      activeCategoryCode &&
      subCategory &&
      !checkIfMenuExists(
        menuCodeMapping[parentCategoryCode]?.children,
        activeCategoryCode,
        subCategory,
      )
    ) {
      router.replace(
        getMenuItemRoute({
          storeBasePath,
          category: menuList[0].code,
          query: persistedFiltersQuery,
        }),
      );
    }
  }, [countryCode, menuList, category, subCategory, menuCodeMapping]);

  const renderItemWithImg = (menuItem) => {
    const categoryImage = menuItem.productsImage || menuItem.attachment || sampleImage;
    if (themedCollections.includes(menuItem.parentItemCode)) {
      return (
        <article className={styles.themeImgWrapper}>
          <Multimedia src={categoryImage} type="image" height={245} width={245} />
          <caption className={styles.imgCaption}>
            {skipTitleHumanization ? menuItem.name : capitalize(menuItem.name)}
          </caption>
        </article>
      );
    }
    return (
      <article className={`${styles.imgWrapper} ${styles.themeImgWrapper}`}>
        <Multimedia src={categoryImage} type="image" height={145} width={145} />
        <caption className={styles.imgCaption}>
          {skipTitleHumanization ? menuItem.name : capitalize(menuItem.name)}
        </caption>
      </article>
    );
  };

  const renderItem = ({ name }) => (
    <span className={`${styles.menuItemName} ${skipTitleHumanization ? "" : styles.textTransform}`}>
      {skipTitleHumanization ? name : capitalize(name)}
    </span>
  );

  const renderDropdownItem = ({
    childItem,
    parentItem,
    parentItemCode,
    itemCode,
    currentLevel,
  }) => {
    return (
      <Link
        href={getMenuItemRoute({
          storeBasePath,
          category: itemCode === ALL_SUBCATEGORY_CODE ? parentItemCode : itemCode,
          query: persistedFiltersQuery,
        })}
        className={`${styles.navLinkSubList} ${
          childItem.code === activeLevelCategory[currentLevel]
            ? `${styles.activeDropdownNav} ${levelClassMapping[currentLevel]}`
            : ""
        }`}
        prefetch={false}
        onClick={() => {
          if (currentLevel === 1) {
            trackMainCategory(childItem);
          } else {
            trackSubcategory(parentItem, childItem);
          }
        }}
      >
        {renderItem({ name: childItem.name })}
      </Link>
    );
  };

  const renderNoChildCategory = (menuItem, currentLevel, { className = "", rowParentItem }) => {
    const category =
      menuItem.itemCode === ALL_SUBCATEGORY_CODE ? menuItem.parentItemCode : menuItem.itemCode;

    return (
      <li
        id="basic-nav-dropdown"
        key={`navlink-${menuItem.id}`}
        className={classnames(className, {
          [styles.menuItem]: shouldShowMenuItemImage(currentLevel),
        })}
      >
        <Link
          passHref
          href={getMenuItemRoute({
            storeBasePath,
            category,
            query: persistedFiltersQuery,
          })}
          className={classnames(
            styles.navLink,
            isSwagCreationShopFlow ? styles.navLinkSwagCreation : "",
            {
              [styles.navLinkImage]: shouldShowMenuItemImage(currentLevel),
            },
          )}
          prefetch={false}
          title={menuItem.name}
          onClick={() => {
            if (currentLevel === 1) {
              trackMainCategory(menuItem);
            } else if (rowParentItem) {
              trackSubcategory(rowParentItem, menuItem);
            }
          }}
        >
          {shouldShowMenuItemImage(currentLevel)
            ? renderItemWithImg(menuItem)
            : renderItem({ name: menuItem.name })}
          <span className={styles.activeNavIndicator} />
        </Link>
      </li>
    );
  };

  const renderDropdown = ({ menuItem, currentLevel, isOtherCategory }) => {
    return (
      <li>
        <DropdownComponent
          className={styles.categorySubMenu}
          styles={styles}
          width={isOtherCategory ? "narrow" : "full"}
          label={
            <DropdownToggle
              className={`${styles.toggleButton} ${styles.navLink} ${
                menuItem.code === activeLevelCategory[currentLevel]
                  ? `${styles.dropdownLabelActive} ${levelClassMapping[currentLevel]}`
                  : ""
              }`}
              dropDownIconClass={styles.dropdownCaret}
              styles={styles}
              showDropdownIcon={!isOtherCategory}
            >
              {renderItem({ name: menuItem.name })}
            </DropdownToggle>
          }
        >
          <DropdownItem
            as="div"
            styles={styles}
            className={`${styles.subCategoryListing} ${
              isOtherCategory ? styles.moreCategoryList : ""
            }`}
          >
            {isOtherCategory ? (
              menuItem.children.map((item) => {
                return (
                  <div className={styles.moreListItem} key={item.name}>
                    {renderDropdownItem({
                      childItem: item,
                      parentItem: menuItem,
                      itemCode: item.itemCode,
                      parentItemCode: item.parentItemCode,
                      currentLevel:
                        menuItem.code == OTHERS_CATEGORY_CODE ? currentLevel : currentLevel + 1,
                    })}
                  </div>
                );
              })
            ) : (
              <div
                className={`${styles.dropdownContainerOuter} ${styles.dropdownContainerOuterFullWidth}`}
              >
                <HeaderContent
                  list={[{ items: menuItem.children, className: styles.headerContentList }]}
                  renderLink={(item) => {
                    return (
                      <div
                        className={`${styles.headerLinkContainer} ${styles.headerLinkContainerColAuto}`}
                        key={item.name}
                      >
                        {" "}
                        {renderDropdownItem({
                          childItem: item,
                          parentItem: menuItem,
                          itemCode: item.itemCode,
                          parentItemCode: item.parentItemCode,
                          currentLevel:
                            menuItem.code == OTHERS_CATEGORY_CODE ? currentLevel : currentLevel + 1,
                        })}
                      </div>
                    );
                  }}
                  gridView
                />
              </div>
            )}
          </DropdownItem>
        </DropdownComponent>
      </li>
    );
  };

  const renderSingleLevelMenuListing = (menuItem, index, currentLevel, rowParentItem) => {
    const menuImageClass = getMenuItemImageClass(currentLevel, menuItem.parentItemCode);

    let className = `${styles.storeNavLink} `;
    className +=
      menuItem.code === activeLevelCategory[currentLevel]
        ? `${styles.active} ${levelClassMapping[currentLevel]} ${menuImageClass}`
        : "";

    if (scrollable) {
      return renderNoChildCategory(menuItem, currentLevel, { className, index, rowParentItem });
    }

    const showDropdown = totalLevel - 1 === currentLevel && isMultiLevelMenu;
    const isOtherCategory = menuItem.code === OTHERS_CATEGORY_CODE;

    if (showCategoryImages) {
      if (isOtherCategory) {
        return currentLevel === 1
          ? renderDropdown({ menuItem, currentLevel, isOtherCategory })
          : menuItem.children.map((newMenuItem) =>
              renderNoChildCategory(newMenuItem, currentLevel, { className, index, rowParentItem }),
            );
      }
      return renderNoChildCategory(menuItem, currentLevel, { className, index, rowParentItem });
    }

    if ((menuItem.children.length === 0 || !showDropdown) && !isOtherCategory) {
      return renderNoChildCategory(menuItem, currentLevel, { className, index, rowParentItem });
    } else if (menuItem.children.length > 0) {
      return renderDropdown({ menuItem, currentLevel, isOtherCategory });
    }
  };

  const renderNavLink = (menuList, currentLevel, rowParentItem) => {
    if (shouldShowMenuItemImage(currentLevel)) {
      const initialSlide = menuList.findIndex(
        (item) => item.code === activeLevelCategory[currentLevel],
      );
      return (
        <section className={styles.sliderWrapper}>
          <PaginationSlider
            sliderRef={paginatedSliderRef}
            sliderSettings={{
              dots: false,
              rows: 1,
              slidesToShow: 5,
              slidesToScroll: 1,
              onInit: () => {
                setTimeout(() => {
                  if (paginatedSliderRef.current) {
                    paginatedSliderRef.current.slickGoTo(initialSlide);
                  }
                }, 100);
              },
            }}
            className={styles.menuSlider}
            containerClass={styles.menuSliderSlick}
          >
            {menuList.map((item, index) =>
              renderSingleLevelMenuListing(item, index, currentLevel, rowParentItem),
            )}
          </PaginationSlider>
        </section>
      );
    }
    return menuList.map((item, index) =>
      renderSingleLevelMenuListing(item, index, currentLevel, rowParentItem),
    );
  };

  const visibleDepth = useMemo(() => {
    const getVisibleDepth = (list, level = 1) => {
      const otherCategory = list.find((item) => item.code === OTHERS_CATEGORY_CODE);

      const activeCategory = [...list, ...(otherCategory?.children || [])].find(
        (item) => item.code === activeLevelCategory[level],
      );

      if (!activeCategory?.children?.length) {
        return level;
      }

      return getVisibleDepth(activeCategory.children, level + 1);
    };

    return getVisibleDepth(finalList);
  }, [finalList, activeLevelCategory]);

  const renderMenu = (list = [], currentLevel = 1, rowParentItem = null) => {
    let showNextLevel =
      !scrollable &&
      (showCategoryImages
        ? orderedMenuItem?.totalLevel - currentLevel > 0 &&
          !(currentLevel === 2 && visibleDepth === 2)
        : orderedMenuItem?.totalLevel - currentLevel > 1 || !isMultiLevelMenu);

    let otherCategory = list.find((menuItem) => menuItem.code === OTHERS_CATEGORY_CODE);

    let activeCategory = [...list, ...(otherCategory?.children || [])].find(
      (menuItem) => menuItem.code === activeLevelCategory[currentLevel],
    );

    return (
      <>
        <div
          className={classnames(styles.navOuterContainer, {
            [styles.navImageContainer]: shouldShowMenuItemImage(currentLevel),
            [styles.navOuterContainerV2]: isNewSwagmagicHost,
            [styles.navWithSwagCreation]: isSwagCreationShopFlow,
          })}
        >
          <ul
            className={`${styles.navContainer} ${styles.storeNavigationContainer} ${
              !scrollable ? styles.navLinksWrap : ""
            }`}
          >
            {renderNavLink(list, currentLevel, rowParentItem)}
            {currentLevel === 1 && !scrollable && <EditCategories />}
          </ul>
        </div>
        {showNextLevel &&
          activeCategory &&
          renderMenu(activeCategory.children, currentLevel + 1, activeCategory)}
      </>
    );
  };
  return finalList?.length ? renderMenu(finalList, 1, null) : null;
};

export default MultiLevelMenu;
